import { CheckCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { EntityConfig } from '../types';

export interface MasterJobStatus {
  id: string;
  code: string;
  display_name: string;
  description: string | null;
  color: string | null;
  is_active: boolean;
  sort_order: number;
  created_at: string;
}

export const masterJobStatusesConfig: EntityConfig<MasterJobStatus> = {
  name: 'masterJobStatuses',
  singularName: 'Status',
  pluralName: 'Job Statuses',
  tableName: 'master_job_statuses',
  keyField: 'id',
  icon: <CheckCircle className="h-4 w-4" />,
  description: 'Job status definitions',
  category: 'master',
  permissionModule: 'settings',

  columns: [
    {
      id: 'sort_order',
      header: 'Order',
      accessorKey: 'sort_order',
      sortable: true,
      width: '80px',
    },
    {
      id: 'code',
      header: 'Code',
      accessorKey: 'code',
      sortable: true,
      cell: (row) => <span className="font-mono text-sm">{row.code}</span>,
    },
    {
      id: 'display_name',
      header: 'Display Name',
      accessorKey: 'display_name',
      sortable: true,
    },
    {
      id: 'color',
      header: 'Color',
      accessorKey: 'color',
      cell: (row) => row.color ? (
        <div className="flex items-center gap-2">
          <div 
            className="w-4 h-4 rounded-full border" 
            style={{ backgroundColor: row.color }}
          />
          <span className="text-sm text-muted-foreground font-mono">{row.color}</span>
        </div>
      ) : <span className="text-muted-foreground">-</span>,
    },
    {
      id: 'description',
      header: 'Description',
      accessorKey: 'description',
      cell: (row) => row.description || <span className="text-muted-foreground">-</span>,
    },
    {
      id: 'is_active',
      header: 'Status',
      accessorKey: 'is_active',
      width: '100px',
      cell: (row) => (
        <Badge variant={row.is_active ? 'default' : 'secondary'}>
          {row.is_active ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
  ],

  formFields: [
    {
      name: 'code',
      label: 'Code',
      type: 'text',
      required: true,
      placeholder: 'e.g., open',
    },
    {
      name: 'display_name',
      label: 'Display Name',
      type: 'text',
      required: true,
      placeholder: 'e.g., Open',
    },
    {
      name: 'color',
      label: 'Color (Hex)',
      type: 'text',
      placeholder: 'e.g., #22c55e',
    },
    {
      name: 'description',
      label: 'Description',
      type: 'textarea',
      gridSpan: 2,
    },
    {
      name: 'sort_order',
      label: 'Sort Order',
      type: 'number',
      defaultValue: 0,
    },
    {
      name: 'is_active',
      label: 'Active',
      type: 'switch',
      defaultValue: true,
    },
  ],

  searchFields: ['code', 'display_name'],
  defaultOrderBy: { field: 'sort_order', ascending: true },

  features: {
    canCreate: true,
    canUpdate: true,
    canDelete: true,
    canBulkDelete: false,
    showActivityLog: false,
  },
};
